import { useCallback, useMemo } from "react";
import {
  useDiscountCodeUpdateMutation,
  useDiscountCodeQuery,
} from "@ticketingplatform/api/dist/discountCode";
import { Form, message } from "antd";
import useApolloErrorFields from "hooks/useApolloErrorFields";
import { useIntl } from "react-intl";
import { useNumberParams } from "hooks/useNumberParams";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import usePrompt from "hooks/usePrompt";
import { ROUTE_PATHS } from "const/paths";

import { initialValues } from "../DiscountCodeCreate/consts";
import { DiscountCodeFormValue } from "../DiscountCodeCreate/partials/DiscountCodeForm/types";

import { UseDiscountCodeEdit, UseDiscountCodeEditReturnType } from "./types";

export const useDiscountCodeEdit: UseDiscountCodeEdit = () => {
  const navigate = useNavigate();
  const intl = useIntl();
  const [form] = Form.useForm<DiscountCodeFormValue>();
  const { id } = useNumberParams(true);

  const { loading: readLoading } = useDiscountCodeQuery({
    variables: { id },
    onCompleted(data) {
      const { discountCode } = data || {};

      if (!discountCode) {
        return;
      }

      form.setFieldsValue({
        input: {
          ...discountCode,
          expireAt: dayjs(discountCode.expireAt),
          events: discountCode.events?.nodes || [],
        },
      });
    },
  });

  const [
    discountCodeUpdateMutation,
    { loading: updateLoading, error: updateError },
  ] = useDiscountCodeUpdateMutation();

  const fields = useApolloErrorFields(updateError);

  usePrompt(updateLoading);

  const onFinish = useCallback(
    async ({
      input: { events, isApplicableToAllEvents, ...input },
    }: DiscountCodeFormValue) => {
      try {
        await discountCodeUpdateMutation({
          variables: {
            id,
            input: {
              ...input,
              isApplicableToAllEvents,
              ...(!isApplicableToAllEvents && {
                eventIds: events.map((event) => event.id),
              }),
            },
          },
        });

        message.success(
          intl.formatMessage({
            defaultMessage: "Discount Code has been updated!",
          })
        );

        navigate(ROUTE_PATHS.DiscountCodeList, {
          state: {
            skipBlock: true,
          },
        });
      } catch {
        message.error(
          intl.formatMessage({
            defaultMessage: "Error. Cannot add Discount Code.",
          })
        );
      }
    },
    [discountCodeUpdateMutation, id, intl, navigate]
  );

  return useMemo<UseDiscountCodeEditReturnType>(
    () => ({
      form: {
        initialValues,
        loading: readLoading || updateLoading,
        fields,
        form,
        onFinish,
      },
    }),
    [fields, form, onFinish, readLoading, updateLoading]
  );
};
